import { useMemo } from 'react';
import { gql } from 'urql';

import { useQueryStable } from '@/hooks/useQueryStable';
import { ErdColumn } from '../types';

const ErdModelColumnsQuery = gql`
  query ErdModelColumns(
    $first: Int!
    $after: String
    $environmentId: BigInt!
    $userId: BigInt
  ) {
    environment(id: $environmentId, userId: $userId) {
      applied {
        models(first: $first, after: $after) {
          pageInfo {
            endCursor
            hasNextPage
          }
          edges {
            node {
              uniqueId
              catalog {
                columns {
                  name
                  type
                  description
                }
              }
            }
          }
        }
      }
    }
  }
`;

interface CatalogColumnNode {
  name: string | null;
  type: string | null;
  description: string | null;
}

interface ModelColumnNode {
  uniqueId: string;
  catalog: {
    columns: CatalogColumnNode[] | null;
  } | null;
}

interface ErdModelColumnsData {
  environment: {
    applied: {
      models: {
        edges: Array<{ node: ModelColumnNode | null }>;
      };
    } | null;
  };
}

interface UseErdModelColumnsParams {
  environmentId: number | undefined;
  userId: number | null;
}

export function useErdModelColumns({
  environmentId,
  userId,
}: UseErdModelColumnsParams) {
  const [result] = useQueryStable<ErdModelColumnsData>({
    query: ErdModelColumnsQuery,
    variables: { first: 500, environmentId, userId },
    pause: !environmentId,
  });

  const columnsByModelId = useMemo(() => {
    const map = new Map<string, ErdColumn[]>();
    if (!result.data?.environment?.applied?.models?.edges) return map;

    for (const edge of result.data.environment.applied.models.edges) {
      if (!edge.node) continue;
      const columns: ErdColumn[] = (edge.node.catalog?.columns ?? []).map((col) => ({
        name: col.name ?? '',
        type: col.type ?? '',
        description: col.description ?? '',
        isPrimaryKey: false,
        isForeignKey: false,
      }));
      map.set(edge.node.uniqueId, columns);
    }

    return map;
  }, [result.data]);

  return {
    columnsByModelId,
    fetching: result.fetching,
    error: result.error,
  };
}
